import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Btn, Eyebrow, Kbd, TankMark } from "../shared/components/redesign";
import styles from "./Login.module.css";

/**
 * Tank Redesign Login screen (PR 2 of the Tank Redesign migration).
 *
 * Source: ~/Downloads/tank-redesign/screens-base.jsx § Screen01Login.
 * Replaces the prior phased terminal Login (`username` → `password` → `loading`)
 * with a single-form flow per the redesign mockup. Behaviour preserved:
 * the `login(username, password)` callback is invoked once with both fields,
 * and `onSuccess` fires on a successful response.
 */
export default function Login({ onSuccess, login }) {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const userRef = useRef(null);

  useEffect(() => {
    userRef.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setIsLoading(true);
    setError(null);
    const result = await login(username.trim(), password);
    if (result.ok) {
      // Brief delay matches the mount-fade duration so the success transition
      // doesn't strobe through a half-rendered state.
      setTimeout(() => onSuccess(), 300);
    } else {
      setError(result.message || t("login.failedFallback", "Login failed"));
      setIsLoading(false);
    }
  };

  const host =
    (typeof window !== "undefined" && window.location?.hostname) ||
    "matrix.aperturelabs.nl";

  return (
    <div className={styles.screen}>
      <div className={styles.vignette} aria-hidden="true" />
      <div className={styles.grid} aria-hidden="true" />

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <TankMark />
          <span className={styles.dot} aria-hidden="true">·</span>
          <span className={styles.poweredBy}>Powered by Aperture Labs</span>
        </div>
        <div className={styles.headerRight}>
          <span className={styles.version}>{host}</span>
        </div>
      </div>

      <form className={styles.column} onSubmit={handleSubmit}>
        <Eyebrow index="01" className={styles.eyebrow}>
          Sign in
        </Eyebrow>

        <div
          className={styles.card}
          style={{
            background: "var(--rd-color-panel)",
            border: "1px solid var(--rd-color-line)",
            borderRadius: "var(--rd-radius-lg)",
            padding: "24px",
          }}
        >
          <div className={styles.terminalLineFirst}>$ tank --connect {host}</div>
          <div className={styles.terminalLine}>$ Operator backend reachable</div>
          <div className={styles.terminalLine}>$ Enter credentials</div>

          <div className={styles.fields}>
            <label className={styles.label} htmlFor="login-username">
              {t("login.username", "Username")}
            </label>
            <input
              id="login-username"
              ref={userRef}
              type="text"
              className={styles.input}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t("login.usernamePlaceholder", "username")}
              autoComplete="username"
              disabled={isLoading}
            />

            <div className={styles.spacer} />

            <label className={styles.label} htmlFor="login-password">
              {t("login.password", "Password")}
            </label>
            <input
              id="login-password"
              type="password"
              className={styles.input}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("login.passwordPlaceholder", "••••••••")}
              autoComplete="current-password"
              disabled={isLoading}
            />
          </div>

          {error && <div className={styles.errorText}>{error}</div>}

          {isLoading && (
            <div className={styles.terminalLineLoading}>
              <span>$ {t("login.connecting", "Connecting")}</span>
              <span className={styles.loadingDots} aria-hidden="true" />
            </div>
          )}

          <Btn
            type="submit"
            kind="primary"
            size="lg"
            className={styles.submit}
            disabled={isLoading || !username.trim() || !password}
          >
            {isLoading
              ? t("login.connecting", "Connecting")
              : t("login.enter", "Sign in")}
          </Btn>

          <div className={styles.hintRow}>
            <Kbd>↵</Kbd>
            <span>{t("login.submitHint", "to submit")}</span>
          </div>
        </div>

        <div className={styles.tenantNote}>
          {t("login.tenant", "Tenant")}:{" "}
          <span className={styles.tenantValue}>{host.split(".")[0]}</span>
        </div>
      </form>
    </div>
  );
}
